import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  claimConflictSchema,
  conversationResponseSchema,
  messageCreatedResponseSchema,
} from '@shared/api';
import type { ConversationDetail, Message } from '@shared/types';
import { toast } from '@web/hooks/useToast';
import { api, HTTPError } from '@web/lib/api';
import { flushBoard } from '@web/lib/boardBuffer';
import { keys } from '@web/lib/keys';
import { appendMessage } from '@web/lib/realtime';

/**
 * Everything that writes to one conversation.
 *
 * Sends are deliberately NOT optimistic. An optimistic bubble says "sent" the
 * instant the key is pressed, and for a message that has to survive a WhatsApp
 * round trip — and can be refused for being outside the 24-hour window — that
 * is a lie the operator acts on. The composer shows the send in flight and the
 * bubble appears when it is real. The wire has no client-side id to reconcile
 * against either, so an optimistic bubble could not be deduped against the
 * broadcast anyway.
 */

export interface ConversationWrite {
  conversationId: string;
  body?: Record<string, unknown>;
}

export type ConversationMutation = UseMutationResult<ConversationDetail, Error, ConversationWrite>;

function useConversationWrite(
  action: string,
  failureText: string,
  conflictText?: string,
): ConversationMutation {
  const client = useQueryClient();

  return useMutation<ConversationDetail, Error, ConversationWrite>({
    mutationFn: async ({ conversationId, body }) => {
      const response = await api.post(
        `/api/conversations/${conversationId}/${action}`,
        conversationResponseSchema,
        body,
      );
      return response.conversation;
    },

    onSuccess: (conversation) => {
      client.setQueryData(keys.conversation(conversation.id), conversation);
      // The board should agree with the screen the operator is looking at
      // before the flush interval gets round to it.
      flushBoard(client);
      void client.invalidateQueries({ queryKey: keys.parked });
      void client.invalidateQueries({ queryKey: keys.board });
    },

    onError: (error) => {
      if (conflictText !== undefined && error instanceof HTTPError && error.status === 409) {
        const conflict = claimConflictSchema.safeParse(error.body);
        const winner = conflict.success ? conflict.data.claimedBy?.name : undefined;
        toast('warn', `${winner ?? 'A colleague'} ${conflictText}`);
        return;
      }
      toast('error', failureText, 'Nothing changed. Try again.');
    },
  });
}

/** Interrupt the assistant from the board or the thread header. */
export function useTakeOver(): ConversationMutation {
  return useConversationWrite('takeover', 'Could not take over', 'is already on this one');
}

/** Hand the conversation back to the assistant, with an instruction for it. */
export function useHandback(): ConversationMutation {
  return useConversationWrite('handback', 'Could not hand back');
}

export function useResolve(): ConversationMutation {
  return useConversationWrite('resolve', 'Could not resolve');
}

/* -------------------------------------------------------------------------- */
/* Sending                                                                     */
/* -------------------------------------------------------------------------- */

export function useSendMessage(
  conversationId: string,
): UseMutationResult<Message, Error, { body: string }> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ body }) => {
      const response = await api.post(
        `/api/conversations/${conversationId}/messages`,
        messageCreatedResponseSchema,
        { body },
      );
      return response.message;
    },
    onSuccess: (message) => {
      // The broadcast will carry this too; `appendMessage` dedupes by id, so
      // whichever arrives first wins and the second is a no-op.
      appendMessage(client, conversationId, message);
    },
    onError: (error) => {
      const detail =
        error instanceof HTTPError && error.status === 502
          ? 'WhatsApp refused it — the 24-hour window may have closed.'
          : 'Nothing was sent. Try again.';
      toast('error', 'Message not sent', detail);
    },
  });
}

/**
 * An internal note. Never leaves the building: the server writes it with
 * `is_private`, no WhatsApp id, and deliberately does not move the board
 * preview — a colleague's private remark showing as the last message on a
 * shared screen is a leak, not a cosmetic bug.
 */
export function useSendNote(
  conversationId: string,
): UseMutationResult<Message, Error, { body: string }> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ body }) => {
      const response = await api.post(
        `/api/conversations/${conversationId}/notes`,
        messageCreatedResponseSchema,
        { body },
      );
      return response.message;
    },
    onSuccess: (message) => {
      appendMessage(client, conversationId, message);
    },
    onError: () => {
      toast('error', 'Note not saved', 'Nothing was written. Try again.');
    },
  });
}
